import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Folder, File, ArrowLeft, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';

type DriveEntry = {
  id: string;
  name: string;
  mimeType: string;
  isFolder: boolean;
  modifiedTime?: string;
  webViewLink?: string;
  parents?: string[];
  driveId?: string;
};

type SharedDrive = { id: string; name: string };

interface GoogleDriveImportModalProps {
  onClose: () => void;
}

const GOOGLE_FOLDER_MIME = 'application/vnd.google-apps.folder';

const GoogleDriveImportModal: React.FC<GoogleDriveImportModalProps> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [activeDriveId, setActiveDriveId] = useState<string | null>(null);
  const [parentId, setParentId] = useState<string>('root');
  const [parentStack, setParentStack] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedFolderIds, setSelectedFolderIds] = useState<Set<string>>(new Set());
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  // customer mapping selections
  const [folderCustomer, setFolderCustomer] = useState<Record<string, string>>({});
  const [fileCustomer, setFileCustomer] = useState<Record<string, string>>({});

  const { data: drivesData, isLoading: drivesLoading } = useQuery({
    queryKey: ['google-drive-drives'],
    queryFn: async () => {
      const response = await apiClient.get('/api/integrations/google-drive/drives');
      return (response as any).data ?? response;
    },
    retry: 1,
  });

  const { data: childrenData, isLoading: childrenLoading, error: childrenError } = useQuery({
    queryKey: ['google-drive-children', activeDriveId, parentId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('parent_id', parentId);
      if (activeDriveId) params.set('drive_id', activeDriveId);
      const response = await apiClient.get(`/api/integrations/google-drive/children?${params.toString()}`);
      return (response as any).data ?? response;
    },
    retry: 1,
  });

  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: apiClient.getCustomers,
    retry: 2,
    staleTime: 30_000,
  });

  const customers = useMemo(() => {
    const payload = (customersData as any)?.data ?? customersData;
    return payload?.customers || [];
  }, [customersData]);

  const drives: SharedDrive[] = Array.isArray(drivesData) ? drivesData : [];
  const entries: DriveEntry[] = useMemo(() => (Array.isArray(childrenData) ? childrenData : []), [childrenData]);

  const filteredEntries = useMemo(() => {
    if (!search) return entries;
    const q = search.toLowerCase();
    return entries.filter((e) => (e.name || '').toLowerCase().includes(q));
  }, [entries, search]);

  const upsertMappingMutation = useMutation({
    mutationFn: async (data: { folder_id?: string; file_id?: string; customer_id: string }) => {
      return apiClient.customRequest('/api/integrations/google-drive/mappings', { method: 'POST', body: data });
    },
  });

  const importMutation = useMutation({
    mutationFn: async (data: { folder_ids?: string[]; file_ids?: string[]; drive_id?: string | null }) => {
      return apiClient.customRequest('/api/integrations/google-drive/import', { method: 'POST', body: data });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      alert(
        data?.documents_imported !== undefined
          ? `Imported ${data.documents_imported} document(s) from Google Drive.`
          : 'Google Drive import completed.'
      );
      onClose();
    },
    onError: (error: any) => {
      alert(`Failed to import from Google Drive: ${error.message}`);
    },
  });

  const isBusy = childrenLoading || drivesLoading || customersLoading || upsertMappingMutation.isPending || importMutation.isPending;

  const toggleFolder = (id: string) => {
    const next = new Set(selectedFolderIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedFolderIds(next);
  };

  const toggleFile = (id: string) => {
    const next = new Set(selectedFileIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedFileIds(next);
  };

  const navigateInto = (folder: DriveEntry) => {
    setParentStack((s) => [...s, { id: parentId, name: parentStack.length === 0 && parentId === 'root' ? 'My Drive' : folder.name }]);
    setParentId(folder.id);
    setSearch('');
  };

  const navigateBack = () => {
    if (parentStack.length === 0) {
      setParentId('root');
      return;
    }
    const last = parentStack[parentStack.length - 1];
    setParentStack((s) => s.slice(0, -1));
    setParentId(last.id);
    setSearch('');
  };

  const handleImport = async () => {
    // Persist mappings first (best-effort)
    const folderOps = Object.entries(folderCustomer).filter(([fid, cid]) => selectedFolderIds.has(fid) && cid);
    const fileOps = Object.entries(fileCustomer).filter(([fid, cid]) => selectedFileIds.has(fid) && cid);

    for (const [folder_id, customer_id] of folderOps) {
      await upsertMappingMutation.mutateAsync({ folder_id, customer_id });
    }
    for (const [file_id, customer_id] of fileOps) {
      await upsertMappingMutation.mutateAsync({ file_id, customer_id });
    }

    importMutation.mutate({
      folder_ids: Array.from(selectedFolderIds),
      file_ids: Array.from(selectedFileIds),
      drive_id: activeDriveId,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Import Google Drive Documents</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Select folders/files, optionally map to customers, then import text for RAG.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-6 border-b border-gray-200 dark:border-gray-700 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={navigateBack} disabled={parentId === 'root' && parentStack.length === 0}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <strong>Folder:</strong> {parentId === 'root' ? 'My Drive' : parentId}
              </div>
            </div>

            <div className="flex-1" />

            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <Select
                value={activeDriveId || '__mydrive__'}
                onValueChange={(v) => {
                  if (v === '__mydrive__') setActiveDriveId(null);
                  else setActiveDriveId(v);
                  setParentId('root');
                  setParentStack([]);
                }}
              >
                <SelectTrigger className="w-full sm:w-[260px]">
                  <SelectValue placeholder="My Drive / Shared drive" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__mydrive__">My Drive</SelectItem>
                  {drives.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search in folder..." />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {childrenError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-3" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Failed to load folder</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {(childrenError as any)?.message || 'Unknown error'}
              </p>
            </div>
          ) : childrenLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {filteredEntries.map((e) => {
                const selected = e.isFolder ? selectedFolderIds.has(e.id) : selectedFileIds.has(e.id);
                return (
                  <div
                    key={e.id}
                    className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => (e.isFolder ? toggleFolder(e.id) : toggleFile(e.id))}
                      className="w-4 h-4 mt-1"
                    />
                    <div className="mt-0.5">
                      {e.isFolder ? (
                        <Folder className="h-5 w-5 text-yellow-600" />
                      ) : (
                        <File className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <button
                          className={`text-left font-medium truncate ${
                            e.isFolder ? 'text-blue-700 dark:text-blue-300 hover:underline' : 'text-gray-900 dark:text-white'
                          }`}
                          onClick={() => e.isFolder && navigateInto(e)}
                          disabled={!e.isFolder}
                        >
                          {e.name}
                        </button>
                        {e.mimeType === GOOGLE_FOLDER_MIME ? null : (
                          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{e.mimeType}</span>
                        )}
                      </div>
                      {selected && (
                        <div className="mt-2">
                          <Select
                            value={(e.isFolder ? folderCustomer[e.id] : fileCustomer[e.id]) || ''}
                            onValueChange={(v) => {
                              if (e.isFolder) setFolderCustomer((m) => ({ ...m, [e.id]: v }));
                              else setFileCustomer((m) => ({ ...m, [e.id]: v }));
                            }}
                          >
                            <SelectTrigger className="w-full sm:w-[360px]">
                              <SelectValue placeholder="Map to customer (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                              {customers.map((c: any) => (
                                <SelectItem key={c.customer_id} value={c.customer_id}>
                                  {c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {e.isFolder
                              ? 'Folder mapping applies to its direct children by default; deeper inheritance updates on incremental sync.'
                              : 'File mapping overrides any inherited folder mapping.'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {filteredEntries.length === 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-10">
                  No items found in this folder.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {selectedFolderIds.size + selectedFileIds.size === 0 ? (
              'Select folders/files to import'
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 inline mr-2 text-green-600" />
                {selectedFolderIds.size} folder(s), {selectedFileIds.size} file(s) selected
              </>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={isBusy}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={isBusy || selectedFolderIds.size + selectedFileIds.size === 0}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
            >
              {isBusy ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Working...
                </>
              ) : (
                'Import'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleDriveImportModal;

